import { useCoolClubMemberQuery } from '@/hooks/react-query/club/use-club'
import { CoolClubSaleFormatted } from '@/types/product'
import { useEffect, useMemo, useState } from 'react'

interface ProductWithCoolClub {
  regular_price: number
  compare_price: number
  sale_percent?: number
  coolclub_sale?: CoolClubSaleFormatted
}

interface UseCoolClubPriceResult<T extends ProductWithCoolClub> {
  productData: T
  isCoolClubSale: boolean
}

/**
 * Hook để xử lý giá CoolClub cho sản phẩm
 * @param product Sản phẩm cần xử lý giá
 * @returns Object chứa sản phẩm đã xử lý và trạng thái CoolClub
 */
export function useCoolClubPrice<T extends ProductWithCoolClub>(
  product: T
): UseCoolClubPriceResult<T> {
  const { data: coolClubMember } = useCoolClubMemberQuery()

  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- standard client-mounted guard to avoid SSR/hydration mismatch
    setIsMounted(true)
  }, [])

  return useMemo(() => {
    // Tạo bản sao của product để tránh thay đổi trực tiếp props
    const productData = { ...product }
    let isCoolClubSale = false

    const ranking_id = isMounted ? coolClubMember?.ranking_id : undefined

    // Kiểm tra điều kiện áp dụng giá CoolClub
    if (
      ranking_id &&
      productData?.coolclub_sale?.active &&
      Array.isArray(productData?.coolclub_sale?.rankings) &&
      productData?.coolclub_sale?.rankings.includes(ranking_id) &&
      typeof productData?.coolclub_sale?.coolclub_price === 'number' &&
      productData?.coolclub_sale?.coolclub_price < productData?.regular_price
    ) {
      // Cập nhật trạng thái và giá
      isCoolClubSale = true
      productData.regular_price = productData.coolclub_sale.coolclub_price

      // Tính lại phần trăm giảm giá
      if (productData.compare_price) {
        productData.sale_percent =
          100 -
          Math.round(
            (productData.regular_price / productData.compare_price) * 100
          )
      }
    }

    return { productData, isCoolClubSale }
  }, [product, coolClubMember?.ranking_id, isMounted])
}
